import { NextRequest, NextResponse } from 'next/server'
import { and, desc, eq } from 'drizzle-orm'
import { db } from '@/db'
import { auditLog, knowledgeEntries, knowledgeEntryRevisions } from '@/db/schema'
import { requireExpertApiUser } from '@/lib/role-guard'
import { inngest } from '@/lib/inngest'
import { normalizeKnowledgeInput } from '@/lib/knowledge'
import type { KnowledgeEntryKind } from '@/db/schema'

type KnowledgeCreateBody = {
  title?: unknown
  slug?: unknown
  kind?: unknown
  bodyMarkdown?: unknown
}

const VALID_KINDS = new Set<KnowledgeEntryKind>(['index', 'log', 'entity', 'concept', 'procedure'])

export async function GET() {
  const guard = await requireExpertApiUser()
  if (!guard.ok) return guard.response

  const rows = await db
    .select({
      id: knowledgeEntries.id,
      slug: knowledgeEntries.slug,
      title: knowledgeEntries.title,
      kind: knowledgeEntries.kind,
      source: knowledgeEntries.source,
      isVerified: knowledgeEntries.isVerified,
      embeddingUpdatedAt: knowledgeEntries.embeddingUpdatedAt,
      updatedAt: knowledgeEntries.updatedAt,
    })
    .from(knowledgeEntries)
    .where(and(eq(knowledgeEntries.tenantId, guard.user.tenantId), eq(knowledgeEntries.isDeleted, false)))
    .orderBy(desc(knowledgeEntries.updatedAt))

  return NextResponse.json({ entries: rows })
}

export async function POST(request: NextRequest) {
  const guard = await requireExpertApiUser()
  if (!guard.ok) return guard.response

  const body = (await request.json().catch(() => null)) as KnowledgeCreateBody | null

  try {
    const kind = parseKind(body?.kind)
    const normalized = normalizeKnowledgeInput({
      title: requireString(body?.title, 'title'),
      slug: optionalString(body?.slug),
      kind,
      bodyMarkdown: requireString(body?.bodyMarkdown, 'bodyMarkdown'),
    })

    const [entry] = await db
      .insert(knowledgeEntries)
      .values({
        tenantId: guard.user.tenantId,
        slug: normalized.slug,
        title: normalized.title,
        kind: normalized.kind,
        bodyMarkdown: normalized.bodyMarkdown,
        flatMarkdown: normalized.flatMarkdown,
        source: 'user',
        isVerified: true,
        createdById: guard.user.id,
        updatedById: guard.user.id,
      })
      .returning()

    await db.insert(knowledgeEntryRevisions).values({
      tenantId: guard.user.tenantId,
      entryId: entry.id,
      revisionNumber: 1,
      title: entry.title,
      bodyMarkdown: entry.bodyMarkdown,
      flatMarkdown: entry.flatMarkdown,
      editedById: guard.user.id,
      changeReason: 'created',
    })

    await db.insert(auditLog).values({
      tenantId: guard.user.tenantId,
      actorId: guard.user.id,
      actorType: 'user',
      action: 'knowledge.created',
      entityType: 'knowledge_entry',
      entityId: entry.id,
      payload: { slug: entry.slug, kind: entry.kind },
    })

    try {
      await inngest.send({
        name: 'knowledge/entry.changed',
        data: { entryId: entry.id, tenantId: guard.user.tenantId, action: 'created' },
      })
    } catch {
      // Inngest unavailable — indexing will not run until re-triggered
    }

    return NextResponse.json({ entry }, { status: 201 })
  } catch (err) {
    const message = err instanceof Error ? err.message : 'Unable to create knowledge entry'
    const status = message.includes('duplicate key') ? 409 : 400
    return NextResponse.json({ error: message }, { status })
  }
}

function parseKind(value: unknown): KnowledgeEntryKind | undefined {
  if (typeof value !== 'string') return undefined
  if (!VALID_KINDS.has(value as KnowledgeEntryKind)) throw new Error('Invalid knowledge entry kind')
  return value as KnowledgeEntryKind
}

function requireString(value: unknown, field: string): string {
  if (typeof value !== 'string') throw new Error(`${field} is required`)
  return value
}

function optionalString(value: unknown): string | undefined {
  return typeof value === 'string' ? value : undefined
}
